import { createSelector } from "@reduxjs/toolkit";

import { FormId } from "../../../../components/enums";
import { RootState } from "../../../../store";
import { UIState } from "../../../../store/HOC/genericUI.slice";
import { generatePhone } from "../../../../utilities/forms";

import { ComponentId as ApplicantComponentId } from "./ApplicantData/enums";
import { ComponentId as PerpetratorComponentId } from "./PerpetratorData/enums";
import {
  FormDataInputs,
  IntroductoryApplicantFormInputs,
  IntroductoryPerpetratorFormInputs,
} from "./introductory.slice";

export const selectIntroductoryApplicantData = (
  store: RootState
): IntroductoryApplicantFormInputs =>
  store.ocVehicle.introductory.data[FormId.INTRODUCTORY_APPLICANT];

export const selectIntroductoryPerpetratorData = (
  store: RootState
): IntroductoryPerpetratorFormInputs =>
  store.ocVehicle.introductory.data[FormId.INTRODUCTORY_PERPETRATOR];

const selectApplicantNameSurname = createSelector(
  selectIntroductoryApplicantData,
  (data) => ({
    name: "Imię i nazwisko",
    value: [
      data[ApplicantComponentId.APPLICANT_NAME],
      data[ApplicantComponentId.APPLICANT_SURNAME],
    ]
      .join(" ")
      .trim(),
  })
);

const selectApplicantTel = createSelector(
  selectIntroductoryApplicantData,
  (data) => ({
    name: "Tel.",
    value: generatePhone(data[ApplicantComponentId.APPLICANT_PHONE]),
  })
);

const selectApplicantEmail = createSelector(
  selectIntroductoryApplicantData,
  (data) => ({
    name: "Email",
    value: data[ApplicantComponentId.APPLICANT_EMAIL],
  })
);

const selectIntroductoryApplicantSummaryData = createSelector(
  selectApplicantNameSurname,
  selectApplicantTel,
  selectApplicantEmail,
  (nameSurname, tel, email) => [nameSurname, tel, email]
);

const selectPerpetratorRegistrationNumber = createSelector(
  selectIntroductoryPerpetratorData,
  (data) => ({
    name: "Numer rejestracyjny",
    value: data[PerpetratorComponentId.PERPETRATOR_REGISTRATION_NUMBER],
  })
);

const selectPerpetratorOcNumber = createSelector(
  selectIntroductoryPerpetratorData,
  (data) => ({
    name: "Numer polisy",
    value: ["PL-OC", data[PerpetratorComponentId.PERPETRATOR_OC_NUMBER]].join(
      " "
    ),
  })
);

const selectPerpetratorNameSurname = createSelector(
  selectIntroductoryPerpetratorData,
  (data) => ({
    name: "Imię i nazwisko",
    value: [
      data[PerpetratorComponentId.PERPETRATOR_NAME],
      data[PerpetratorComponentId.PERPETRATOR_SURNAME],
    ]
      .join(" ")
      .trim(),
  })
);

const selectPerpetratorTel = createSelector(
  selectIntroductoryPerpetratorData,
  (data) => ({
    name: "Tel.",
    value: generatePhone(data[PerpetratorComponentId.PERPETRATOR_PHONE]),
  })
);

const selectPerpetratorEmail = createSelector(
  selectIntroductoryPerpetratorData,
  (data) => ({
    name: "Email",
    value: data[PerpetratorComponentId.PERPETRATOR_EMAIL],
  })
);

const selectIntroductoryPerpetratorSummaryData = createSelector(
  selectPerpetratorRegistrationNumber,
  selectPerpetratorOcNumber,
  selectPerpetratorNameSurname,
  selectPerpetratorTel,
  selectPerpetratorEmail,
  (registrationNumber, ocNumber, nameSurname, tel, email) => [
    registrationNumber,
    ocNumber,
    nameSurname,
    tel,
    email,
  ]
);

export const selectSummaryData = createSelector(
  selectIntroductoryPerpetratorSummaryData,
  selectIntroductoryApplicantSummaryData,
  (perpetrator, applicant) => ({ perpetrator, applicant })
);

export const selectFormData = (store: RootState): FormDataInputs =>
  store.ocVehicle.introductory.data;

export const selectUIData = (store: RootState): UIState =>
  store.ocVehicle.introductory.ui;
